import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, extractError, API_BASE } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CalendarPlus, Trash2, User, Video, Clock, LogOut, Stethoscope, CircleDot, XCircle, Download, Sparkles, ClipboardList, Activity, FileText } from "lucide-react";
import AiNoteModal from "@/components/AiNoteModal";
import PrescribeModal from "@/components/PrescribeModal";
import PatientEngagementModal from "@/components/PatientEngagementModal";
import PatientChartModal from "@/components/PatientChartModal";
import CareDigestPreview from "@/components/CareDigestPreview";

export default function SpecialistDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [newSlot, setNewSlot] = useState({ start_iso: "", duration_min: 45, session_mode: "1:1" });
  const [noteFor, setNoteFor] = useState(null); // bookingId for AI note modal
  const [prescribeFor, setPrescribeFor] = useState(null); // {bid, patientName}
  const [engagementFor, setEngagementFor] = useState(null); // {userId, name}
  const [chartFor, setChartFor] = useState(null); // {patientId, patientName, bookingId}

  const refresh = () => {
    api.get("/specialist/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/specialist/slots").then((r) => setSlots(r.data)).catch(() => {});
    api.get("/specialist/bookings").then((r) => setBookings(r.data)).catch(() => {});
  };
  useEffect(() => {
    refresh();
  }, []);

  const addSlot = async () => {
    if (!newSlot.start_iso) return toast.error("Pick a date and time");
    try {
      await api.post("/specialist/slots", {
        start_iso: new Date(newSlot.start_iso).toISOString(),
        duration_min: newSlot.duration_min,
        session_mode: newSlot.session_mode,
      });
      toast.success("Slot published");
      setNewSlot({ ...newSlot, start_iso: "" });
      refresh();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const remove = async (sid) => {
    try {
      await api.delete(`/specialist/slots/${sid}`);
      refresh();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const requestRecording = async (bid) => {
    try {
      await api.post(`/bookings/${bid}/recording/request`);
      toast.success("Recording requested. The patient will be asked to consent before joining.");
      refresh();
    } catch (e) { toast.error(extractError(e)); }
  };

  const cancelRecording = async (bid) => {
    try {
      await api.post(`/bookings/${bid}/recording/cancel`);
      toast.info("Recording request cancelled.");
      refresh();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="min-h-screen bg-[color:var(--mano-bg)]" data-testid="specialist-page">
      <header className="bg-white border-b border-[color:var(--mano-border)] px-5 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/specialist" className="flex items-center gap-2" data-testid="spec-brand">
          <div className="h-9 w-9 rounded-xl bg-[color:var(--mano-primary)] text-white flex items-center justify-center">
            <Stethoscope size={18} />
          </div>
          <div>
            <div className="font-display leading-none">Manodayam · Specialist</div>
            <div className="uppercase-label mt-1">{me?.expert?.specialization || user?.role}</div>
          </div>
        </Link>
        <button
          data-testid="spec-logout"
          onClick={async () => { await logout(); nav("/login"); }}
          className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-[color:var(--mano-border)] hover:bg-[color:var(--mano-border)]/40 text-sm"
        >
          <LogOut size={14} /> Log out
        </button>
      </header>

      <main className="max-w-5xl mx-auto px-5 lg:px-10 py-8 space-y-8">
        <section className="rounded-3xl bg-[color:var(--mano-hero)] text-white p-7 lg:p-9 grain relative overflow-hidden">
          <span className="uppercase-label text-white/70">Welcome</span>
          <h1 className="font-display text-3xl mt-2">{me?.expert?.name || user?.name}</h1>
          <p className="text-white/80 mt-2 max-w-lg">
            Publish availability, manage upcoming consults, and join sessions with your patients.
          </p>
          <div className="mt-5 grid grid-cols-3 gap-3 max-w-md">
            <Metric v={slots.filter((s) => !s.booked).length} l="Open slots" />
            <Metric v={slots.filter((s) => s.booked).length} l="Booked" />
            <Metric v={bookings.length} l="Consults" />
          </div>
        </section>

        <CareDigestPreview />

        <section className="rounded-2xl bg-white border border-[color:var(--mano-border)] p-6">
          <div className="uppercase-label inline-flex items-center gap-1"><CalendarPlus size={14} /> Publish a new slot</div>
          <div className="mt-4 grid sm:grid-cols-4 gap-3">
            <div className="sm:col-span-2">
              <label className="uppercase-label">Date & time</label>
              <input
                data-testid="spec-slot-datetime"
                type="datetime-local"
                value={newSlot.start_iso}
                onChange={(e) => setNewSlot({ ...newSlot, start_iso: e.target.value })}
                className="mt-2 w-full h-11 px-3 rounded-xl border border-[color:var(--mano-border)] bg-[color:var(--mano-bg)]"
              />
            </div>
            <div>
              <label className="uppercase-label">Duration (min)</label>
              <input
                data-testid="spec-slot-duration"
                type="number"
                min="15"
                step="5"
                value={newSlot.duration_min}
                onChange={(e) => setNewSlot({ ...newSlot, duration_min: Number(e.target.value) })}
                className="mt-2 w-full h-11 px-3 rounded-xl border border-[color:var(--mano-border)] bg-[color:var(--mano-bg)]"
              />
            </div>
            <div>
              <label className="uppercase-label">Mode</label>
              <select
                data-testid="spec-slot-mode"
                value={newSlot.session_mode}
                onChange={(e) => setNewSlot({ ...newSlot, session_mode: e.target.value })}
                className="mt-2 w-full h-11 px-3 rounded-xl border border-[color:var(--mano-border)] bg-[color:var(--mano-bg)]"
              >
                <option value="1:1">1 : 1</option>
                <option value="1:many">1 : many</option>
              </select>
            </div>
          </div>
          <button
            data-testid="spec-add-slot"
            onClick={addSlot}
            className="mt-4 inline-flex items-center gap-1 px-5 py-2.5 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white font-semibold transition-colors"
          >
            <CalendarPlus size={14} /> Publish slot
          </button>
        </section>

        <section>
          <div className="uppercase-label inline-flex items-center gap-1"><Clock size={14} /> My published slots</div>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            {slots.length === 0 && (
              <div className="col-span-full text-center py-6 rounded-2xl border border-dashed border-[color:var(--mano-border)] text-[color:var(--mano-ink-soft)]">
                No slots yet — publish your first above.
              </div>
            )}
            {slots.map((s) => (
              <div key={s.id} className="rounded-xl border border-[color:var(--mano-border)] bg-white p-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold">{new Date(s.start_iso).toLocaleString()}</div>
                  <div className="text-xs text-[color:var(--mano-ink-soft)]">
                    {s.session_mode} · {s.booked ? "Booked" : "Open"}
                  </div>
                </div>
                {!s.booked && (
                  <button data-testid={`spec-del-slot-${s.id}`} onClick={() => remove(s.id)} className="p-2 rounded-full text-[color:var(--mano-high)] hover:bg-[color:var(--mano-high)]/10">
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="uppercase-label inline-flex items-center gap-1"><User size={14} /> Upcoming consults</div>
          <div className="mt-3 space-y-2">
            {bookings.length === 0 && (
              <div className="rounded-2xl border border-dashed border-[color:var(--mano-border)] p-6 text-center text-[color:var(--mano-ink-soft)]">
                No consults yet.
              </div>
            )}
            {bookings.map((b) => (
              <div key={b.id} data-testid={`spec-booking-${b.id}`} className="rounded-xl border border-[color:var(--mano-border)] bg-white p-4 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="font-semibold">{b.patient?.name || "Patient"}</div>
                  <div className="text-xs text-[color:var(--mano-ink-soft)]">
                    {new Date(b.start_iso).toLocaleString()} · {b.session_type}
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs">
                    {b.recording_requested ? (
                      b.recording_patient_consent ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[color:var(--mano-normal)]/15 text-[color:var(--mano-normal)] font-semibold">
                          <CircleDot size={10} /> Recording consented
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[color:var(--mano-mild)]/20 text-[color:var(--mano-mild)] font-semibold">
                          <CircleDot size={10} /> Awaiting patient consent
                        </span>
                      )
                    ) : null}
                    {b.recording_available && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[color:var(--mano-border)] text-[color:var(--mano-ink-soft)] font-semibold">
                        Clip saved · {(b.recording_size / (1024*1024)).toFixed(1)}MB
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {b.recording_requested ? (
                    <button
                      data-testid={`spec-rec-cancel-${b.id}`}
                      onClick={() => cancelRecording(b.id)}
                      className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-[color:var(--mano-border)] text-xs font-semibold"
                    >
                      <XCircle size={12} /> Cancel recording
                    </button>
                  ) : (
                    <button
                      data-testid={`spec-rec-request-${b.id}`}
                      onClick={() => requestRecording(b.id)}
                      className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-[color:var(--mano-primary)] text-[color:var(--mano-primary)] text-xs font-semibold"
                    >
                      <CircleDot size={12} /> Request recording
                    </button>
                  )}
                  {b.recording_available && (
                    <>
                      <a
                        data-testid={`spec-rec-download-${b.id}`}
                        href={`${API_BASE}/bookings/${b.id}/recording`}
                        className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-[color:var(--mano-ink)] text-white text-xs font-semibold"
                      >
                        <Download size={12} /> Download
                      </a>
                      <button
                        data-testid={`spec-ai-note-${b.id}`}
                        onClick={() => setNoteFor(b.id)}
                        className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-[color:var(--mano-mild)] text-[color:var(--mano-ink)] text-xs font-semibold"
                      >
                        <Sparkles size={12} />
                        {b.ai_note_status === "saved" ? "Note saved"
                          : b.ai_note_status === "draft" ? "Review AI note"
                          : b.ai_note_status === "generating" || b.ai_note_status === "queued" ? "AI note · drafting…"
                          : b.ai_note_status === "failed" ? "AI note · retry"
                          : "AI note"}
                      </button>
                    </>
                  )}
                  <button
                    data-testid={`spec-therapy-activity-${b.id}`}
                    onClick={() => setEngagementFor({ userId: b.user_id, name: b.patient?.name })}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-[color:var(--mano-border)] text-xs font-semibold"
                    title="See what therapies this patient has opened"
                  >
                    <Activity size={12} /> Activity
                  </button>
                  <button
                    data-testid={`spec-chart-${b.id}`}
                    onClick={() => setChartFor({ patientId: b.user_id, patientName: b.patient?.name, bookingId: b.id })}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-[color:var(--mano-border)] text-xs font-semibold hover:border-[color:var(--mano-primary)] hover:text-[color:var(--mano-primary)]"
                    title="Open patient chart & write encounter note"
                  >
                    <FileText size={12} /> Chart
                  </button>
                  <button
                    data-testid={`spec-prescribe-${b.id}`}
                    onClick={() => setPrescribeFor({ bid: b.id, patientName: b.patient?.name })}
                    className={`inline-flex items-center gap-1 px-3 py-2 rounded-full text-xs font-semibold transition-colors ${
                      b.prescribed_at
                        ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                        : "bg-white border border-[color:var(--mano-primary)] text-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary)]/5"
                    }`}
                  >
                    <ClipboardList size={12} /> {b.prescribed_at ? "Care plan sent" : "Prescribe"}
                  </button>
                  <Link
                    to={`/session/${b.id}`}
                    data-testid={`spec-join-${b.id}`}
                    className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white text-sm font-semibold transition-colors"
                  >
                    <Video size={14} /> Join session
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>

      {noteFor && (
        <AiNoteModal
          bookingId={noteFor}
          onClose={() => setNoteFor(null)}
          onSaved={refresh}
        />
      )}
      {prescribeFor && (
        <PrescribeModal
          bookingId={prescribeFor.bid}
          patientName={prescribeFor.patientName}
          onClose={() => setPrescribeFor(null)}
          onDone={() => { setPrescribeFor(null); refresh(); }}
        />
      )}
      {engagementFor && (
        <PatientEngagementModal
          userId={engagementFor.userId}
          onClose={() => setEngagementFor(null)}
        />
      )}
      {chartFor && (
        <PatientChartModal
          patientId={chartFor.patientId}
          patientName={chartFor.patientName}
          bookingId={chartFor.bookingId}
          canAuthor
          onClose={() => setChartFor(null)}
        />
      )}
    </div>
  );
}

function Metric({ v, l }) {
  return (
    <div className="rounded-xl bg-white/10 p-3">
      <div className="font-display text-2xl">{v}</div>
      <div className="uppercase-label text-white/70">{l}</div>
    </div>
  );
}
